/**
 * Delivery history, in words a desk reads.
 *
 * The raw record has a row per recipient per run, and a row per run when
 * nothing was attempted. The old table printed the status word and a column
 * header that did not fit it: a run kept in the archive with no email attempted
 * read "Recipient not recorded", and a run whose schedule had since been
 * deleted read "Deleted schedule" beside it. Here every row says who, what, and
 * what happened, and says plainly when the answer is "nothing was sent".
 */
import type {
  ReportDeliveryHistory,
  ReportDeliveryHistoryItem,
  ReportSchedule,
} from '@/pages/pipeline-intelligence/types'
import { endSentence, oneLine, parseInstant } from './format'

export type DeliveryOutcome = 'failed' | 'sent' | 'delivered' | 'opened' | 'sending' | 'kept' | 'unknown'
export type DeliveryTone = 'ok' | 'warn' | 'bad' | 'muted'

export type DeliveryRow = {
  id: string
  runId: string
  scheduleId: string
  scheduleName: string
  /** The schedule that made this run no longer exists. */
  scheduleDeleted: boolean
  reportId: string | null
  reportTitle: string
  /** An address, or the plain statement that nothing was addressed. */
  who: string
  /** True when there is no recipient because no email was attempted. */
  noEmail: boolean
  outcome: DeliveryOutcome
  statusLabel: string
  tone: DeliveryTone
  at: string | null
  detail: string
  /** The archive still holds the generated file. */
  downloadable: boolean
  format: 'pdf' | 'xlsx'
}

/** A sending row older than this has not been heard back about and needs a person. */
export const STUCK_SENDING_HOURS = 2

const LABELS: Record<DeliveryOutcome, { label: string; tone: DeliveryTone }> = {
  failed: { label: 'Failed', tone: 'bad' },
  sent: { label: 'Sent', tone: 'ok' },
  delivered: { label: 'Delivered', tone: 'ok' },
  opened: { label: 'Opened', tone: 'ok' },
  sending: { label: 'Sending', tone: 'warn' },
  kept: { label: 'Kept, not emailed', tone: 'muted' },
  unknown: { label: 'Unknown', tone: 'muted' },
}

function outcomeOf(status: string): DeliveryOutcome {
  switch (status.toLowerCase()) {
    case 'failed':
    case 'bounced':
      return 'failed'
    case 'sent':
      return 'sent'
    case 'delivered':
      return 'delivered'
    case 'opened':
      return 'opened'
    case 'sending':
      return 'sending'
    case 'retained':
      return 'kept'
    default:
      return 'unknown'
  }
}

export function deliveryRows(
  history: Pick<ReportDeliveryHistory, 'items' | 'capabilities'> | undefined,
  schedules: ReportSchedule[],
  now: Date = new Date(),
): DeliveryRow[] {
  if (!history) return []
  const scheduleById = new Map(schedules.map((schedule) => [schedule.id, schedule]))
  const receipts = history.capabilities?.providerDeliveryReceipts === true

  return history.items.map((item: ReportDeliveryHistoryItem): DeliveryRow => {
    const outcome = outcomeOf(String(item.status ?? ''))
    const schedule = scheduleById.get(item.scheduleId)
    // The server says so when it knows; an older server does not, and a schedule
    // missing from the list we hold is the next best evidence.
    const scheduleDeleted = item.scheduleExists === false || (item.scheduleExists === undefined && !schedule)
    const recipient = item.recipient?.trim() || null
    const at = item.sentAt ?? item.completedAt ?? item.scheduledFor ?? null
    const reason = oneLine(item.error)
    const bounced = String(item.status).toLowerCase() === 'bounced'

    let detail: string
    switch (outcome) {
      case 'failed':
        detail = bounced
          ? 'Bounced back from the recipient’s mail server.'
          : recipient
            ? endSentence(`Not delivered: ${reason || 'the mail provider gave no reason'}`)
            : endSentence(`The scheduled run failed before a report was made: ${reason || 'no reason was recorded'}`)
        break
      case 'sending': {
        const started = parseInstant(at)?.valueOf() ?? NaN
        const stuck = Number.isFinite(started) && now.valueOf() - started > STUCK_SENDING_HOURS * 3_600_000
        detail = stuck
          ? 'Handed to the mail provider and nothing has come back since. Someone needs to check it.'
          : 'Being sent.'
        break
      }
      case 'sent':
        detail = receipts
          ? 'Accepted by the mail server. Waiting for the provider to confirm delivery.'
          : 'Accepted by the mail server. This deployment does not receive delivery confirmations, so it cannot say more.'
        break
      case 'delivered':
        detail = 'Delivered to the recipient’s mail server.'
        break
      case 'opened':
        detail = item.openCount > 1 ? `Opened ${item.openCount} times.` : 'Opened.'
        break
      case 'kept': {
        if (scheduleDeleted) {
          detail = 'Kept in the archive. The schedule that made it has since been deleted.'
        } else if (schedule?.deliveryMode === 'none') {
          detail = 'Kept in the archive. This schedule does not email.'
        } else {
          detail = 'Kept in the archive. No email was attempted for this run.'
        }
        break
      }
      default:
        detail = 'The mail ledger holds a status this page does not recognize.'
    }

    const { label, tone } = LABELS[outcome]
    return {
      id: item.attemptId ?? item.runId,
      runId: item.runId,
      scheduleId: item.scheduleId,
      scheduleName:
        scheduleDeleted && item.scheduleName !== 'Deleted schedule'
          ? `${item.scheduleName} (deleted)`
          : item.scheduleName,
      scheduleDeleted,
      reportId: item.reportId,
      reportTitle: item.reportTitle || (outcome === 'failed' ? 'No report was made' : 'Report not recorded'),
      who: recipient ?? (outcome === 'failed' ? 'No recipient reached' : 'No email attempted'),
      noEmail: !recipient,
      outcome,
      statusLabel: label,
      tone,
      at,
      detail,
      downloadable: Boolean(item.reportDownloadable && item.reportId),
      format: String(item.reportFormat ?? '').toLowerCase() === 'xlsx' ? 'xlsx' : 'pdf',
    }
  })
}

export type DeliveryFilter = 'all' | 'failed' | 'kept' | 'sent'

export const DELIVERY_FILTERS: Array<{ id: DeliveryFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'failed', label: 'Failed' },
  { id: 'kept', label: 'Kept, not emailed' },
  { id: 'sent', label: 'Sent' },
]

export function matchesDeliveryFilter(row: DeliveryRow, filter: DeliveryFilter): boolean {
  if (filter === 'all') return true
  if (filter === 'failed') return row.outcome === 'failed'
  if (filter === 'kept') return row.outcome === 'kept'
  return row.outcome === 'sent' || row.outcome === 'delivered' || row.outcome === 'opened' || row.outcome === 'sending'
}

export type DeliverySummary = {
  total: number
  sent: number
  delivered: number
  opened: number
  failed: number
  sending: number
  kept: number
}

/** Counted from the rows on screen, so the tiles and the table cannot disagree. */
export function summarizeDeliveries(rows: DeliveryRow[]): DeliverySummary {
  const count = (outcome: DeliveryOutcome) => rows.filter((row) => row.outcome === outcome).length
  return {
    total: rows.length,
    sent: count('sent'),
    delivered: count('delivered'),
    opened: count('opened'),
    failed: count('failed'),
    sending: count('sending'),
    kept: count('kept'),
  }
}

/** The caveat under the table: which receipts this deployment has and has not reported. */
export function receiptsNote(history: Pick<ReportDeliveryHistory, 'capabilities'> | undefined): string | null {
  const caps = history?.capabilities
  if (!caps) return null
  if (caps.providerDeliveryReceipts && caps.openTracking) return null
  if (!caps.providerDeliveryReceipts) {
    return 'No provider delivery receipt or open event has been recorded on this deployment, so Delivered and Opened stay empty until the provider reports them.'
  }
  return 'No open event has been recorded on this deployment, so Opened stays empty until the provider reports one.'
}


/** Whether a delivery row matches every word of a search. */
export function matchesDeliveryQuery(row: DeliveryRow, query: string): boolean {
  const tokens = query.toLowerCase().split(/\s+/).filter(Boolean)
  if (!tokens.length) return true
  const text = [row.who, row.reportTitle, row.scheduleName, row.statusLabel, row.detail].join(' ').toLowerCase()
  return tokens.every((token) => text.includes(token))
}
